// frontend/src/components/auth/LoginModal.jsx
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ROUTES } from "../../config";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Input from "../common/Input";
import { AlertIcon } from "../common/Icon";
import "./LoginModal.css";

const MIN_PASSWORD = 8; // must match backend signup validation

/**
 * Login / signup dialog.
 * Props: open, onClose, onSuccess(user) (called instead of onClose after a
 * successful login/signup when provided), initialMode ("login" | "signup")
 */
export default function LoginModal({ open, onClose, onSuccess, initialMode = "login" }) {
  const { login, signup, error, clearError } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [secretCode, setSecretCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState("");

  const isSignup = mode === "signup";

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setDisplayName("");
    setSecretCode("");
    setLocalError("");
    clearError();
  };

  const switchMode = () => {
    setMode((m) => (m === "login" ? "signup" : "login"));
    setPassword("");
    setLocalError("");
    clearError();
  };

  const handleClose = () => {
    resetForm();
    setMode(initialMode);
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    clearError();

    if (!email.trim() || !password) {
      setLocalError("Email and password are required.");
      return;
    }
    if (isSignup && password.length < MIN_PASSWORD) {
      setLocalError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (isSignup && !secretCode.trim()) {
      setLocalError("An access code is required to sign up.");
      return;
    }

    setLoading(true);
    try {
      const user = isSignup
        ? await signup(email.trim(), password, displayName.trim() || undefined, secretCode.trim())
        : await login(email.trim(), password);
      resetForm();
      if (onSuccess) onSuccess(user);
      else onClose?.();
    } catch {
      // Message is surfaced through AuthContext.error
    } finally {
      setLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isSignup ? "Create your account" : "Welcome back"}
      description={
        isSignup
          ? "PAWNI is in a closed research preview. You'll need an access code from the team."
          : "Log in to pick up where you left off."
      }
      width={460}
      className="login-modal"
    >
      <form className="login-form" onSubmit={handleSubmit} noValidate>
        {displayError && (
          <div className="login-form__error" role="alert">
            <AlertIcon size={16} />
            <span>{displayError}</span>
          </div>
        )}

        {isSignup && (
          <>
            <Input
              id="login-secret"
              label="Access code"
              type="password"
              value={secretCode}
              onChange={(e) => setSecretCode(e.target.value)}
              placeholder="Enter your access code"
              autoComplete="off"
              hint={
                <>
                  No code? Write to <a href="mailto:thepawniproject@gmail.com">thepawniproject@gmail.com</a>.
                </>
              }
              required
            />
            <Input
              id="login-name"
              label="Display name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How should we greet you?"
              autoComplete="name"
            />
          </>
        )}

        <Input
          id="login-email"
          label="Email"
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          data-autofocus={!isSignup || undefined}
          required
        />

        <Input
          id="login-password"
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={isSignup ? `At least ${MIN_PASSWORD} characters` : "Your password"}
          autoComplete={isSignup ? "new-password" : "current-password"}
          minLength={isSignup ? MIN_PASSWORD : undefined}
          required
        />

        <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
          {isSignup ? "Create account" : "Log in"}
        </Button>

        {isSignup && (
          <p className="login-form__legal">
            By creating an account you agree to the <Link to={ROUTES.terms} onClick={handleClose}>Terms</Link> and
            confirm you have read the <Link to={ROUTES.privacy} onClick={handleClose}>Privacy Policy</Link>. You
            must be 16 or older.
          </p>
        )}

        <p className="login-form__switch">
          {isSignup ? "Already have an account?" : "New to PAWNI?"}{" "}
          <button type="button" className="login-form__link" onClick={switchMode}>
            {isSignup ? "Log in" : "Create an account"}
          </button>
        </p>
      </form>
    </Modal>
  );
}
